import { useState } from 'react';
import { resolveAristoImageUrl } from './aristo-image';
import { toRichBlocks } from './rich-text';

/**
 * Enunciado (ou complemento) de uma questão da Aristo: texto em parágrafos e
 * as imagens embutidas (`![](images/questions/…png)`) no lugar em que aparecem
 * — **o mesmo do `QuestionCard` do `enamed-feedback-lp`**. O texto pode vir em
 * HTML do banco da Aristo; `toRichBlocks` extrai só o texto, sem injetar HTML.
 */
export function RichText({
  value,
  className,
  questionNumber,
}: {
  value: string | null | undefined;
  className: string;
  questionNumber: number;
}) {
  const blocks = toRichBlocks(value);
  if (blocks.length === 0) return null;

  let imageCount = 0;

  return (
    <div className={className}>
      {blocks.map((block, index) => {
        if (block.kind === 'text') return <p key={index}>{block.text}</p>;
        imageCount += 1;
        return (
          <QuestionImage
            key={index}
            path={block.path}
            alt={
              block.alt ||
              `Imagem ${imageCount} do enunciado da questão ${questionNumber}`
            }
          />
        );
      })}
    </div>
  );
}

/**
 * Uma imagem do enunciado — em geral tabela, eletrocardiograma ou radiografia.
 *
 * **O tamanho é limitado pelo CSS** (`.qcard-image`): uma radiografia em
 * resolução original empurraria tudo para baixo. Como o limite pode apertar um
 * ECG a ponto de atrapalhar a leitura, **a imagem é um link para ela mesma em
 * outra aba**, no tamanho real.
 *
 * Se não carregar, dá lugar a um aviso: sem ela a questão pode não ter
 * resposta possível, e um ícone de imagem quebrada não diz isso a ninguém.
 */
function QuestionImage({ path, alt }: { path: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  const url = resolveAristoImageUrl(path);

  if (!url || failed) {
    return (
      <p className="qcard-image-missing">
        Não foi possível carregar a imagem desta questão.
      </p>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="qcard-image-link"
      title="Abrir a imagem em tamanho real"
    >
      <img
        src={url}
        alt={alt}
        loading="lazy"
        className="qcard-image"
        onError={() => setFailed(true)}
      />
    </a>
  );
}
